import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {ZedHost, ZedLeaving} from "./zed";
import {flyScreen, LEAVE_MS} from "./fly";

// The stage every screen plays on: when `screenKey` changes, the old screen's pieces fly off to the left (frozen
// as it was when it left) while the new one's fly in from the right, and one Zed glides between them.
const Screen = ({children, mode}) => {
    const ref = useRef(null);
    useLayoutEffect(() => {
        if (mode === 'still') return undefined; // arrived without a transition
        const cls = mode === 'out' ? 'fly-out' : 'fly-in';
        flyScreen(ref.current, cls);
        if (mode !== 'in') return undefined;
        // screens that load data first show their real content a moment later: fly that in when it arrives
        const mo = new MutationObserver(() => flyScreen(ref.current, cls));
        mo.observe(ref.current, {childList: true, subtree: true});
        const stop = setTimeout(() => mo.disconnect(), 1500);
        return () => { mo.disconnect(); clearTimeout(stop); };
    }, [mode]);
    // same structure in and out, so the leaving screen is the same instance, not a fresh copy
    return <div ref={ref} style={mode === 'out' ? {position: 'absolute', top: 0, left: 0, right: 0, pointerEvents: 'none', zIndex: 4} : undefined}>
        <ZedLeaving active={mode === 'out'}>{children}</ZedLeaving>
    </div>;
};

let screenId = 0;

// `still`: the first screen appears as it is, without flying in (say, after the browser's own back animation).
export const FlyStage = ({screenKey, still = false, children}) => {
    const [screens, setScreens] = useState(() => [{id: screenId++, key: screenKey, mode: still ? 'still' : 'in'}]);
    const last = useRef({}); // each screen's last content, so a leaving screen keeps showing what it showed
    const current = screens.find(sc => sc.mode !== 'out');
    if (current.key !== screenKey) {
        setScreens(prev => [...prev.map(sc => sc.mode !== 'out' ? {...sc, mode: 'out'} : sc), {id: screenId++, key: screenKey, mode: 'in'}]);
    } else {
        last.current[current.id] = children;
    }
    useEffect(() => {
        if (!screens.some(sc => sc.mode === 'out')) return undefined;
        const t = setTimeout(() => setScreens(prev => {
            const kept = prev.filter(sc => sc.mode !== 'out');
            Object.keys(last.current).forEach(id => { if (!kept.some(sc => String(sc.id) === id)) delete last.current[id]; });
            return kept;
        }), LEAVE_MS);
        return () => clearTimeout(t);
    }, [screens]);
    // clip pieces flying past the edges, so the page never scrolls sideways
    return <div style={{overflowX: 'hidden'}}>
        <ZedHost>
            {screens.map(sc => <Screen key={sc.id} mode={sc.mode}>{last.current[sc.id]}</Screen>)}
        </ZedHost>
    </div>;
};
